import { z } from 'zod'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { DashboardRepository } from '../../application/DashboardRepository'
import type { OperationsSnapshot } from '../../domain/operationsSnapshot'

const missionsSchema = z.object({
  active: z.coerce.number().int().nonnegative(),
  planned: z.coerce.number().int().nonnegative(),
  withAnomalies: z.coerce.number().int().nonnegative(),
  pendingItems: z.coerce.number().int().nonnegative(),
  blockedItems: z.coerce.number().int().nonnegative(),
})

const invoicesSchema = z.object({
  unpaid: z.coerce.number().int().nonnegative(),
  unpaidCents: z.coerce.number().int().nonnegative(),
  overdue: z.coerce.number().int().nonnegative(),
})

const contractsSchema = z.object({
  active: z.coerce.number().int().nonnegative(),
  awaitingSignature: z.coerce.number().int().nonnegative(),
  expiringSoon: z.coerce.number().int().nonnegative(),
})

const quotesSchema = z.object({
  sent: z.coerce.number().int().nonnegative(),
  accepted: z.coerce.number().int().nonnegative(),
})

const operatorsSchema = z.object({ online: z.coerce.number().int().nonnegative() })

const attentionSchema = z.object({
  id: z.string(),
  kind: z.enum(['QUOTE_ACCEPTED_WITHOUT_CONTRACT', 'INVOICE_OVERDUE', 'CONTRACT_WITHOUT_PERIOD']),
  title: z.string(),
  description: z.string(),
  tone: z.enum(['danger', 'warning', 'info']),
  occurredAt: z.string(),
})

const activitySchema = z.object({
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  description: z.string(),
  occurredAt: z.string(),
})

const snapshotSchema = z.object({
  missions: missionsSchema.nullable(),
  operators: operatorsSchema.nullable(),
  invoices: invoicesSchema.nullable(),
  contracts: contractsSchema.nullable(),
  quotes: quotesSchema.nullable(),
  attention: z.array(attentionSchema),
  activity: z.array(activitySchema),
  computedAt: z.string(),
})

export function createSupabaseDashboardRepository(client: AppSupabaseClient): DashboardRepository {
  const rpcClient = client as unknown as SupabaseClient
  return {
    async getSnapshot(): Promise<OperationsSnapshot> {
      const response = (await rpcClient.rpc('operations_dashboard_v1')) as {
        data?: unknown
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'DASHBOARD_SNAPSHOT_FAILED')
      const parsed = snapshotSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('DASHBOARD_SNAPSHOT_RESPONSE_INVALID')
      const { missions, operators, invoices, contracts, quotes, ...rest } = parsed.data
      // Un bloc absent le reste : `null` dit « pas le droit de lire », et zéro
      // dirait « rien à faire ». Les deux ne se confondent pas.
      return {
        ...rest,
        ...(missions ? { missions } : {}),
        ...(operators ? { operators } : {}),
        ...(invoices ? { invoices } : {}),
        ...(contracts ? { contracts } : {}),
        ...(quotes ? { quotes } : {}),
      }
    },
  }
}
